import { Observable, of, catchError, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { EventDTO } from '../../../../dto/event/event.dto';
import { environment } from '../../../../environment/environment';
import { AuthService } from '../../../../services/auth/auth.service';
import {
  GroupCalendarEventCell,
  GroupCalendarMonthData,
  GroupCalendarDayRow
} from '../../../../components/duna/duna-events-group-calendar/group-calendar-settings.interface';
import { parseJsonInput } from '../../../../components/duna/duna-events-group-calendar/group-calendar-settings.utils';

function getEventDisplayName(event: EventDTO): string {
  const shortDesc = event.description?.short_description;
  if (shortDesc) {
    const da = shortDesc['da']?.translation_text;
    if (da) return da;
    const first = Object.values(shortDesc)[0]?.translation_text;
    if (first) return first;
  }
  return 'Event';
}

function resolveLocationKey(
  event: EventDTO,
  selectedLocationGuids: string[]
): string {
  const locGuid = event.predefined_location?.guid || event.predefined_location_guid;
  if (locGuid && selectedLocationGuids.includes(locGuid)) {
    return locGuid;
  }
  return 'other';
}

function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getIsoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function buildMonthGrid(
  year: number,
  month: number,
  events: GroupCalendarEventCell[],
  selectedLocationGuids: string[]
): GroupCalendarDayRow[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows: GroupCalendarDayRow[] = [];
  let prevWeek = -1;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateKey = formatDateKey(date);
    const weekNumber = getIsoWeek(date);
    const showWeekNumber = weekNumber !== prevWeek;
    prevWeek = weekNumber;

    const cells: Record<string, GroupCalendarEventCell | null> = {};
    for (const guid of selectedLocationGuids) {
      cells[guid] = null;
    }
    cells['other'] = null;

    const dayEvents = events.filter(e => e.start_date.startsWith(dateKey));
    const dayGroupIds = new Set<number>();
    let dayGroupId: number | null = null;
    let dayGroupName: string | null = null;

    for (const ev of dayEvents) {
      cells[ev.locationKey] = ev;
      if (ev.user_group_id != null && ev.user_group_id > 0) {
        dayGroupIds.add(ev.user_group_id);
        if (dayGroupId == null) {
          dayGroupId = ev.user_group_id;
          dayGroupName = ev.user_group_name ?? null;
        }
      }
    }

    rows.push({
      dateKey,
      dayOfMonth: day,
      weekNumber,
      showWeekNumber,
      cells,
      user_group_id: dayGroupId,
      user_group_name: dayGroupName,
      user_group_ids: dayGroupIds.size > 0 ? [...dayGroupIds] : undefined
    });
  }

  return rows;
}

export function group_calendar_events(service: ComponentDataSourceService) {
  return {
    name: 'Group Calendar Events',
    description: 'Events for group calendar month grid',
    execute: (params?: LoadDataParams): Observable<GroupCalendarMonthData> => {
      const year = Number(params?.queryParams?.['year'] ?? new Date().getFullYear());
      const month = Number(params?.queryParams?.['month'] ?? new Date().getMonth());
      const rawEventsOnly = params?.queryParams?.['eventsOnly'];
      const eventsOnly = rawEventsOnly === true || rawEventsOnly === 'true' || rawEventsOnly === 1 || rawEventsOnly === '1';
      const selectedLocationGuids = parseJsonInput<string[]>(
        params?.queryParams?.['calendarLocationGuids'],
        []
      );

      const explicitStart = params?.queryParams?.['startDate'];
      const explicitEnd = params?.queryParams?.['endDate'];
      let startDate: string;
      let endDate: string;
      if (explicitStart && explicitEnd) {
        startDate = String(explicitStart);
        endDate = String(explicitEnd);
      } else if (eventsOnly) {
        startDate = `${year}-01-01`;
        endDate = `${year}-12-31`;
      } else {
        startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }

      const authService = service.injector.get(AuthService);
      const user = authService.getCurrentUser() as Record<string, unknown> | null;
      const orgIdRaw =
        user?.['organizations_id'] ??
        (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
      const orgId = typeof orgIdRaw === 'number' ? orgIdRaw : parseInt(String(orgIdRaw ?? ''), 10);

      
      const queryParams: Record<string, string> = {
        startDate,
        endDate,
        group_calendar_only: 'true',
        _nocache: String(Date.now()),
        filters: JSON.stringify([
          { column: 'startDate', value: startDate },
          { column: 'endDate', value: endDate }
        ])
      };
      if (Number.isFinite(orgId) && orgId > 0) {
        queryParams['organization_ids'] = String(orgId);
      }

      return service.http.get<EventDTO[]>(`${environment.apiUrl}/events`, { params: queryParams }).pipe(
        map(result => {
          const list = (Array.isArray(result) ? result : Object.values(result ?? {}) as EventDTO[])
            .filter(event => event.deleted !== true && event.is_active !== false)
            .filter(event => event.user_group_id != null && Number(event.user_group_id) > 0);
          const events: GroupCalendarEventCell[] = list.map(event => ({
            guid: event.guid,
            name: getEventDisplayName(event),
            start_date: event.start_date,
            end_date: event.end_date,
            is_shown: event.is_shown !== false,
            user_group_id: event.user_group_id ?? null,
            user_group_name: event.user_group_name ?? null,
            locationKey: resolveLocationKey(event, selectedLocationGuids),
            template_id: event.group_calendar_template_id ?? null
          }));

          return {
            year,
            month,
            days: eventsOnly ? [] : buildMonthGrid(year, month, events, selectedLocationGuids),
            events
          };
        }),
        catchError((error: unknown) => {
          // Older API versions return 404 when no events match — treat as empty month
          if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
            console.warn('[group_calendar_events] Failed to load events:', error);
          }
          return of({
            year,
            month,
            days: eventsOnly ? [] : buildMonthGrid(year, month, [], selectedLocationGuids),
            events: []
          });
        })
      );
    }
  };
}
